import find from "ramda/es/find"
import findIndex from "ramda/es/findIndex"
import debounce from "lodash/debounce"
import { interpolatePath } from "d3-interpolate-path"
import { select } from "d3-selection"
import { scaleLinear, scaleTime } from "d3-scale"
import { axisBottom, axisLeft } from "d3-axis"
import { extent, min, max } from "d3-array"
import { line } from "d3-shape"
import { transition } from "d3-transition"
import { timeYear, timeMonth } from "d3-time"
import { format } from "d3-format"
import {
  isValid,
  closestTo,
  isEqual,
  subYears,
  subMonths,
  format as formatDate,
} from "datastory-shared/utils/date"
import { ChartTooltip } from "datastory-shared/data-ui"
import { isBrowser } from "datastory-shared/utils/browser"

const closest = (el, fn) => el && (fn(el) ? el : closest(el.parentNode, fn))

const lineChart = () => {
  const annotationHolders = {}
  let model = {
    transitionDuration: 250,
    margin: { top: 45, right: 60, bottom: 60, left: 50 },
    resultTooltipWidth: 270,
  }
  let width
  let height
  let element
  let svg
  let g
  let mouseRect
  let pollLinesHolder
  let linesHolder
  let lineLabelsHolder
  let resultHolder
  let resultLine
  let resultDateLabel
  let resultTooltip
  let resultTooltipWrapper
  let resultTooltipList
  let clip
  let l
  let x
  let y
  let xAxis
  let yAxis
  let xAxisContainer
  let yAxisContainer
  let chartId
  let resultsDate
  let tip
  const numberFormat = format(".1f")

  const getId = d => `${d.id}-${d.pollster}-${d.date}`

  const getValidResultsDate = (date, event) => {
    const { trends } = model
    const xDomain = getXDomain()
    const dates = trends[0].values
      .map(d => d.date)
      .filter(d => d >= xDomain[0] && d <= xDomain[1])
    return closestTo(date || (event && x.invert(event.x)), dates)
  }

  function onMouseMove(event) {
    if (!model.showLineTooltip) return
    const dimensions = this.parentNode.getBoundingClientRect()
    // -10 is to account for the padding on the x range
    const mouseDate = x.invert(
      event.pageX - dimensions.left - model.margin.left - 10
    )
    const xDomain = x.domain()

    const { values } = model.trends[0]
    const minDate = xDomain[0] > values[0].date ? xDomain[0] : values[0].date
    const maxDate =
      xDomain[1] < values[values.length - 1].date
        ? xDomain[1]
        : values[values.length - 1].date

    const yDomain = y.domain()
    const mouseValue = y.invert(event.y - dimensions.top - model.margin.top)

    if (
      minDate <= mouseDate &&
      mouseDate <= maxDate &&
      yDomain[0] <= mouseValue &&
      mouseValue <= yDomain[1]
    ) {
      resultHolder.style("opacity", 1)
      select("body").style("cursor", "pointer")
      drawResultsLine(getValidResultsDate(mouseDate, event), 50)
    } else {
      resultHolder.style("opacity", 0)
      select("body").style("cursor", "default")
    }
  }

  const getX = d => x(d.date)
  const getY = d => y(d.value)

  const getOpacity = d => {
    if (!model.selected || !model.selected.length) return 1
    return model.selected.indexOf(d.id) > -1 ? 1 : model.opacityNonSelected
  }

  const getPointOpacity = d => {
    if (!model.selected || !model.selected.length) return model.pointOpacity
    return model.selected.indexOf(d.id) > -1
      ? model.pointOpacity
      : model.opacityNonSelected
  }

  const getColor = d =>
    d.color || find(m => m.id === (d.id || getId(d)), model.metaParties).color

  const drawLines = data => {
    const t = transition().duration(model.transitionDuration)

    const line = linesHolder.selectAll(".line").data(data, d => d.id)

    line
      .enter()
      .append("path")
      .attr("class", "line party")
      .style("stroke-opacity", getOpacity)
      .attr("d", d => l(d.values))
      .merge(line)
      .transition(t)
      .attrTween("d", function (d) {
        const previous = select(this).attr("d")
        const current = l(d.values)
        return interpolatePath(previous, current)
      })
      .style("stroke-opacity", getOpacity)
      .style("stroke", getColor)

    line.exit().transition(t).style("stroke-opacity", 0).remove()
  }

  const drawPollLines = data => {
    const t = transition().duration(model.transitionDuration)

    const line = pollLinesHolder
      .selectAll(".poll-line")
      .data(data, d => d.id + d.pollster)

    line
      .enter()
      .append("path")
      .attr("class", "poll-line line party")
      .style("stroke", model.colors.gray[500])
      .style("stroke-width", 1)
      .style("stroke-dasharray", "3, 1.5")
      .attr("d", d => l(d.values))
      .merge(line)
      .transition(t)
      .style("stroke", model.colors.gray[500])
      .attr("d", d => l(d.values))

    line.exit().transition(t).style("stroke-opacity", 0).remove()
  }

  const drawPoints = data => {
    const points = pollLinesHolder.selectAll(".point").data(
      data.filter(d => !isNaN(x(d.date))),
      getId
    )

    points
      .enter()
      .append("circle")
      .attr("class", "point")
      .attr("r", 3.5)
      .style("opacity", 0)
      .attr("fill", getColor)
      .attr("cx", d => x(d.date))
      .attr("cy", d => y(d.value))
      .attr("stroke", model.colors.white)
      .attr("stroke-width", 1.5)
      .on("mouseover", function (event) {
        const d = event.currentTarget.__data__

        if (typeof model.pointTooltipFormat === "function") {
          d.date_formatted = formatDate(new Date(d.date))
          tip.show(model.pointTooltipFormat(d))
        }

        if (model.pointTooltipSticky) {
          const dimensions = this.getBoundingClientRect()
          const bodyDimensions = document.body.getBoundingClientRect()
          const bodyTop = Math.abs(bodyDimensions.top)
          const bodyLeft = Math.abs(bodyDimensions.left)
          tip.position(
            {
              pageX: bodyLeft + dimensions.left + dimensions.width / 2,
              pageY: bodyTop + dimensions.top,
            },
            model.pointTooltipOffset
          )
        }

        pollLinesHolder
          .selectAll(".point")
          .filter(p => getId(p) !== getId(d))
          .interrupt()
          .transition()
          .duration(model.transitionDuration)
          .style("opacity", 0.2)
      })

      .on("mousemove", () => {
        if (!model.pointTooltipSticky)
          tip.position(event, model.pointTooltipOffset)
      })
      .on("mouseout", function () {
        pollLinesHolder
          .selectAll(".point")
          .interrupt()
          .transition()
          .duration(model.transitionDuration)
          .style("opacity", getPointOpacity)
        tip.hide()
      })
      .merge(points)
      .transition()
      .duration(model.transitionDuration)
      .style("opacity", getPointOpacity)
      .attr("fill", getColor)
      .attr("stroke", model.colors.white)
      .attr("cx", d => x(d.date))
      .attr("cy", d => y(d.value))

    points
      .exit()
      .transition()
      .duration(model.transitionDuration)
      .style("opacity", 0)
      .remove()
  }

  const drawLineLabels = (data, duration = model.transitionDuration) => {
    const labels = lineLabelsHolder
      .selectAll(".line-label")
      .data(data, d => d.id)

    labels
      .enter()
      .append("foreignObject")
      .html(d => d.html)
      .attr("x", getX)
      .attr("width", "100")
      .attr("height", "50")
      .attr("opacity", 0)
      .attr("class", "line-label")
      .attr("color", getColor)
      .attr("transform", d => {
        const filtered = data.filter(dd => isEqual(d.date, dd.date))
        const i = findIndex(dd => d.id === dd.id, filtered)
        return `translate(2, ${getResultYPosition(d, i, filtered)})`
      })
      .merge(labels)
      .html(d => d.html)
      .transition()
      .duration(duration)
      .attr("x", getX)
      .attr("opacity", getOpacity)
      .attr("color", getColor)
      .attr("transform", d => {
        const filtered = data.filter(dd => isEqual(d.date, dd.date))
        const i = findIndex(dd => d.id === dd.id, filtered)
        return `translate(2, ${getResultYPosition(d, i, filtered)})`
      })

    labels.exit().transition().duration(duration).attr("opacity", 0).remove()
  }

  const drawLineDots = (data, duration = model.transitionDuration) => {
    const t = transition().duration(duration)

    const dots = resultHolder
      .selectAll(".line-dot")
      .data(data.filter(getY), d => d.id)

    dots
      .enter()
      .append("circle")
      .attr("class", "line-dot")
      .attr("cx", 0)
      .attr("cy", getY)
      .attr("r", "4")
      .attr("opacity", 0)
      .attr("fill", getColor)
      .attr("stroke", model.colors.white)
      .attr("stroke-width", 1.5)
      .merge(dots)
      .transition(t)
      .attr("cy", getY)
      .attr("opacity", getOpacity)
      .attr("fill", getColor)
      .attr("stroke", model.colors.white)

    dots.exit().transition(t).attr("opacity", 0).remove()
  }

  const drawResultTooltip = (data, date, xPos) => {
    const isNarrowWidth = width / 2 <= model.resultTooltipWidth - 20
    const isNarrowHeight = height < 400
    const tooltipWidth = isNarrowWidth ? 170 : model.resultTooltipWidth

    resultTooltip
      .attr("x", xPos >= width / 2 ? -tooltipWidth : -5)
      .attr("y", isNarrowHeight ? -10 : 30)
      .attr("width", tooltipWidth)

    resultLine.attr("stroke", model.colors.gray[300])

    resultDateLabel.style("background-color", model.colors.whiteAlpha[900])
    resultTooltipWrapper
      .style("background-color", model.colors.gray[700])
      .style("color", model.colors.whiteAlpha[900])

    const rows = resultTooltipList
      .selectAll(".result-tooltip-row")
      .data([...data.filter(getY)].reverse())

    const rowsEnter = rows
      .enter()
      .append("div")
      .attr("class", "result-tooltip-row")
      .style("overflow", "hidden")
      .style("padding-top", "0.25rem")
      .style("font-size", ".875rem")

    rows.exit().remove()

    const dots = rows
      .merge(rowsEnter)
      .selectAll(".result-tooltip-dot")
      .data(d => [d])

    dots
      .enter()
      .append("div")
      .attr("class", "result-tooltip-dot")
      .style("float", "left")
      .style("margin", "6px 5px 0 0")
      .style("width", "12px")
      .style("height", "12px")
      .style("border-radius", "50%")
      .style("background-color", getColor)
      .merge(dots)
      .style("background-color", getColor)

    dots.exit().remove()

    const names = rows
      .merge(rowsEnter)
      .selectAll(".result-tooltip-name")
      .data(d => [d])

    names
      .enter()
      .append("div")
      .attr("class", "result-tooltip-name")
      .style("float", "left")
      .text(d => {
        const name =
          model.metaParties.find(party => party.id === d.id)?.name || ""
        return isNarrowWidth
          ? `${name.slice(0, 6)}…`
          : model.metaParties.find(party => party.id === d.id).name
      })
      .merge(names)
      .text(d => {
        const name =
          model.metaParties.find(party => party.id === d.id)?.name || ""
        return isNarrowWidth
          ? `${name.slice(0, 6)}…`
          : model.metaParties.find(party => party.id === d.id).name
      })

    names.exit().remove()

    const values = rows
      .merge(rowsEnter)
      .selectAll(".result-tooltip-value")
      .data(d => [d])

    values
      .enter()
      .append("div")
      .attr("class", "result-tooltip-value")
      .style("float", "right")
      .text(d => `${numberFormat(d.value)}%`)
      .merge(values)
      .text(d => `${numberFormat(d.value)}%`)

    values.exit().remove()
  }

  const getResultYPosition = (d, i, data) => {
    let pos = y(d.value) - 13

    // If it's not the first item,
    // make sure we have enough space
    if (i) {
      const prevPos = getResultYPosition(data[i - 1], i - 1, data)
      const diff = prevPos - pos
      pos = diff > 15 ? pos : pos - 15 + diff
    }
    return pos
  }

  const getResultsData = (data, date) =>
    data
      .filter(d => d.values && d.values.length)
      .map(d => {
        const img = `${d.id.split(":")[1]}.svg`
        const bgUrl = `/static/images/parties/svg/${img}`

        const bgColor = model.colors.whiteAlpha[900]
        const style = [
          "display: inline-block",
          "line-height: 18px",
          "height: 18px",
          "padding-left: 20px",
          "font-weight:bold",
          "font-size: 0.875rem",
          "font-family: basicSans, basicSansInitial, sans-serif",
          `background: ${bgColor} url(${bgUrl}) 0 50% / 18px no-repeat`,
        ].join("; ")
        const index =
          date === "latest"
            ? d.values.length - 1
            : findIndex(d => isEqual(d.date, date), d.values)
        const datum = d.values[index] || {}

        return {
          ...datum,
          color: d.color,
          id: d.id,
          html: `<div style="${style}">${numberFormat(datum.value)}%</div>`,
        }
      })
      .sort((a, b) => (a.value || 0) - (b.value || 0))

  const drawResultsLine = (date, duration = model.transitionDuration) => {
    const t = transition().duration(duration)
    const { trends } = model
    const data = getResultsData(trends, date)
    const xPos = x(date)
    resultHolder.transition(t).attr("transform", `translate(${xPos}, 0)`)
    resultDateLabel.text(formatDate(new Date(date)))

    drawLineDots(data, duration)
    drawResultTooltip(data, date, xPos)
  }

  const getYDomain = () => {
    const { showConfidenceIntervals, trends, yDomain } = model
    // let selectedTrends

    // if it's passed as an array, just return it
    if (Array.isArray(yDomain)) return yDomain

    return [
      0,
      max(trends, c =>
        max(c.values, d => (showConfidenceIntervals && d.ciu) || d.value)
      ),
    ]
  }

  const getXDomain = () => {
    const { xDomain, trends } = model
    const xDomains = model.xDomains.slice()
    const preset = find(x => x.id === xDomain, xDomains)
    const today = new Date()
    let presetExtent
    let unit
    let value

    // copy the extent if we found the preset
    if (preset) presetExtent = [preset.extent[0], preset.extent[1]]

    // if we can parse the extent as dates, return those
    if (
      presetExtent &&
      isValid(new Date(presetExtent[0])) &&
      isValid(new Date(presetExtent[1]))
    )
      return [new Date(presetExtent[0]), new Date(presetExtent[1])]

    // if we found a preset, but the extent is custom, try to parse it
    if (presetExtent) {
      // try to parse the end or default to today
      presetExtent[1] = isValid(new Date(presetExtent[1]))
        ? new Date(presetExtent[1])
        : today

      // try to parse starting date, if it works return the extent
      if (isValid(new Date(presetExtent[0])))
        return [new Date(presetExtent[0]), presetExtent[1]]

      // extract unit and value to subtract from the todays date
      // unit is one character at the end, supported "m" and "y"
      unit = presetExtent[0].slice(-1)
      value = +presetExtent[0].slice(0, presetExtent[0].length - 1)

      if (unit === "y") presetExtent[0] = subYears(today, value)
      else if (unit === "m") presetExtent[0] = subMonths(today, value)
      else presetExtent[0] = min(trends[0].values, d => d.date)

      return presetExtent
    }

    return extent(trends[0].values, d => d.date)
  }

  const updateAnnotations = data => {
    const bottomData = data.filter(a => a.holder === "bottom")
    const topData = data.filter(a => a.holder === "top")
    const htmlData = data.filter(a => a.holder === "html")
    drawAnnotations(bottomData, annotationHolders.bottom)
    drawAnnotations(topData, annotationHolders.top)
    drawAnnotations(htmlData, annotationHolders.html)
  }

  const drawAnnotations = (data, holder) => {
    const t = transition().duration(model.transitionDuration)
    const annotations = holder.selectAll(".annotation").data(data, d => d.id)

    annotations
      .enter()
      .append(holder.node().tagName === "g" ? "g" : "div")
      .attr("class", "annotation")
      .attr("opacity", 0)
      .each(function (d) {
        drawAnnotation(d, select(this))
      })
      .merge(annotations)
      .transition(t)
      .attr("opacity", 1)
      .each(function (d) {
        drawAnnotation(d, select(this))
      })

    annotations.exit().transition(t).attr("opacity", 0)
  }

  const drawAnnotation = (annotation, holder) => {
    switch (annotation.type) {
      case "line":
        drawAnnotationLine(annotation, holder)
        break
      case "text":
        drawAnnotationText(annotation, holder)
        break
      case "html":
        drawAnnotationHtml(annotation, holder)
        break
    }
  }

  const drawAnnotationLine = (annotation, holder) => {
    let line = holder.select("line")
    let attrs

    if (!line.size()) line = holder.append("line")

    if (annotation.y) {
      // Horizontal line
      attrs = {
        x1: x(x.domain()[0]),
        x2: x(x.domain()[1]),
        y1: y(annotation.y),
        y2: y(annotation.y),
      }
    } else if (annotation.x && isValid(annotation.x)) {
      // Vertical line
      attrs = {
        x1: x(annotation.x),
        x2: x(annotation.x),
        y1: y(y.domain()[0]),
        y2: y(y.domain()[1]),
      }
    } else {
      attrs = {
        x1: x(annotation.x1),
        x2: x(annotation.x2),
        y1: y(annotation.y1),
        y2: y(annotation.y2),
      }
    }

    if (annotation.offsetX1) attrs.x1 += annotation.offsetX1
    if (annotation.offsetX2) attrs.x2 += annotation.offsetX2
    if (annotation.offsetY1) attrs.y1 += annotation.offsetY1
    if (annotation.offsetY2) attrs.y2 += annotation.offsetY2

    for (const prop in attrs) {
      line.attr(prop, attrs[prop])
    }

    for (const prop in annotation.style) {
      line.style(prop, annotation.style[prop])
    }
  }

  const drawAnnotationText = (annotation, holder) => {
    let foreignObject = holder.select("foreignObject")

    if (!foreignObject.size()) {
      foreignObject = holder.append("foreignObject")
      foreignObject.append("xhtml:div")
    }

    const attrs = {
      x: x(annotation.x),
      y: annotation.y ? y(annotation.y) : height - 20,
      width: parseFloat(annotation.width),
      height: parseFloat(annotation.height),
      offsetX: 0,
      offsetY: 0,
    }

    if (annotation.offsetX) attrs.x += annotation.offsetX
    if (annotation.offsetY) attrs.y += annotation.offsetY

    if (attrs.x + attrs.width - attrs.offsetX > width)
      attrs.x = width - attrs.width - attrs.offsetX

    for (const prop in attrs) {
      foreignObject.attr(prop, attrs[prop])
    }

    const text = foreignObject.select("div").html(annotation.html)
    for (const prop in annotation.style) {
      text.style(prop, annotation.style[prop])
    }
  }

  const drawAnnotationHtml = (annotation, holder) => {
    holder.html(annotation.html).style("position", "absolute")

    let left =
      model.margin.left + (annotation.x ? x(annotation.x) : x(x.domain()[0]))
    if (annotation.offsetX) left += annotation.offsetX
    annotation.style.left = `${left}px`

    let top =
      model.margin.top + (annotation.y ? y(annotation.y) : y(y.domain()[0]))
    if (annotation.offsetY) top += annotation.offsetY
    annotation.style.top = `${top}px`

    for (const prop in annotation.style) {
      holder.style(prop, annotation.style[prop])
    }

    if (annotation.tooltipContent) {
      holder
        .on("mouseenter", function () {
          const dimensions = this.getBoundingClientRect()
          const bodyDimensions = document.body.getBoundingClientRect()
          const bodyTop = Math.abs(bodyDimensions.top)
          const bodyLeft = Math.abs(bodyDimensions.left)
          tip.show(annotation.tooltipContent)
          tip.position({
            pageX: bodyLeft + dimensions.left + dimensions.width / 2,
            pageY: bodyTop + dimensions.top,
          })
        })
        .on("mouseleave", tip.hide)
    }
  }

  const isSelected = ({ id }) => {
    const { selected } = model
    return !selected.length || selected.indexOf(id) !== -1
  }

  const update = (m = {}, event) => {
    model = Object.assign(model, m)
    const { margin, pollLines, visibilityNonSelected, annotations } = model
    let { trends, polls } = model

    polls =
      visibilityNonSelected === "hidden" ? polls.filter(isSelected) : polls

    trends =
      visibilityNonSelected === "hidden" ? trends.filter(isSelected) : trends

    const totalWidth = element.offsetWidth || 960
    // let totalHeight = totalWidth / 1.92
    let totalHeight = totalWidth > 600 ? totalWidth / 2 : totalWidth / 1.5
    const oTransitionDuration = model.transitionDuration
    model.transitionDuration = 0

    if (totalHeight < 400) totalHeight = 400

    width = totalWidth - margin.left - margin.right
    height = totalHeight - margin.top - margin.bottom

    svg.attr("width", totalWidth).attr("height", totalHeight)

    g.attr("transform", "translate(" + margin.left + "," + margin.top + ")")
    mouseRect.attr("width", width).attr("height", height)
    clip.attr("width", width).attr("height", height)

    x.range([10, width])
    y.range([height, 0])

    yAxis.tickSizeInner(-width)

    const getLocalisedMonth = d => formatDate(new Date(d), { month: "long" })

    if (width < 420) {
      if (model.xDomain == "6m") {
        xAxis.ticks(timeMonth.every(2))
      } else if (model.xDomain == "complete") {
        xAxis
          .ticks(timeYear.every(4))
          .tickFormat(d => formatDate(new Date(d), { year: "numeric" }))
      } else {
        xAxis.ticks(timeMonth.every(12))
      }
    } else {
      if (model.xDomain == "6m") {
        xAxis.ticks(timeMonth.every(1)).tickFormat(getLocalisedMonth)
      } else if (model.xDomain == "12m") {
        xAxis.ticks(timeMonth.every(2)).tickFormat(getLocalisedMonth)
      } else {
        xAxis
          .ticks(timeYear.every(1))
          .tickFormat(d => formatDate(new Date(d), { year: "numeric" }))
      }
    }

    xAxisContainer.attr("transform", "translate(0," + height + ")")

    if (model.showLineTooltip) resultLine.attr("y2", height)

    model.transitionDuration = oTransitionDuration

    x.domain(getXDomain())
    y.domain(getYDomain())

    yAxis.tickValues(model.yTickValues)
    xAxisContainer.call(xAxis)
    yAxisContainer.call(yAxis)

    if (pollLines) {
      drawPollLines(pollLines)
      drawPoints(polls)
    }
    drawLines(trends)
    const latestValueDate =
      trends[0].values.length &&
      getValidResultsDate(
        trends[0].values[trends[0].values.length - 1].date,
        event
      )
    resultsDate = resultsDate
      ? getValidResultsDate(resultsDate, event)
      : latestValueDate
    drawLineLabels(getResultsData(trends, "latest"))
    if (model.showLineTooltip && resultsDate) drawResultsLine(resultsDate)
    if (annotations && annotations.length) updateAnnotations(annotations)

    tip = ChartTooltip({
      styles: {
        borderRadius: "0.25rem",
        background: model.colors.gray[700],
        color: model.colors.whiteAlpha[900],
      },
      hasArrow: false,
    })
  }

  const resize = event => {
    update({}, event)
  }

  const create = (el, m = {}) => {
    model = Object.assign(model, m)
    element = el
    chartId = `linechart-${+new Date()}`
    tip = ChartTooltip({
      styles: {
        borderRadius: "0.25rem",
        background: model.colors.gray[700],
        color: model.colors.whiteAlpha[900],
      },
      hasArrow: false,
    })

    element.style.position = "relative"
    element.id = chartId

    svg = select(element).append("svg").style("cursor", "default")

    g = svg
      .append("g")
      .attr("id", `g-${chartId}`)
      .style("pointer-events", "all")
      .on("mousemove", onMouseMove)

    if (model.showLineTooltip)
      g.on("mouseleave", () => {
        resultHolder.style("opacity", 0)
        select("body").style("cursor", "default")
      })

    mouseRect = g
      .append("rect")
      .attr("class", "click-capture")
      .style("visibility", "hidden")
      .attr("x", 0)
      .attr("y", 0)

    clip = g.append("clipPath").attr("id", `clip-${chartId}`).append("rect")
    yAxisContainer = g.append("g").attr("class", "axis axis--y")
    xAxisContainer = g.append("g").attr("class", "axis axis--x")
    annotationHolders.bottom = g.append("g")
    linesHolder = g
      .append("g")
      .attr("class", "lines-holder")
      .attr("clip-path", `url(#clip-${chartId})`)
    pollLinesHolder = g
      .append("g")
      .attr("class", "poll-lines-holder")
      .attr("clip-path", `url(#clip-${chartId})`)
    lineLabelsHolder = g.append("g").attr("class", "lines-labels-holder")

    annotationHolders.top = g.append("g")
    annotationHolders.html = select(element)
      .append("div")
      .style("position", "absolute")
      .style("top", 0)
      .style("left", 0)
      .style("z-index", 1)

    x = scaleTime().nice()
    y = scaleLinear()

    l = line()
      .x(d => x(d.date))
      .y(d => y(d.value))

    xAxis = axisBottom(x)
    yAxis = axisLeft(y)
      .tickValues(model.yTickValues)
      .tickFormat(d => `${d}%`)
      .tickSizeOuter(0)
      .tickPadding(10)

    if (model.showLineTooltip) {
      resultHolder = g
        .append("g")
        .style("opacity", 0)
        .style("transition", "opacity .15s ease-in-out")

      resultLine = resultHolder
        .append("line")
        .attr("x1", "0")
        .attr("y1", "0")
        .attr("x2", "0")
        .attr("stroke-width", "1")
        .attr("stroke", model.colors.gray[500])

      resultDateLabel = resultHolder
        .append("foreignObject")
        .attr("class", "result-date-label")
        .attr("x", -60)
        .attr("y", -24)
        .attr("width", 120)
        .attr("height", 18)
        .append("xhtml:div")
        .style("font-size", "14px")
        .style("text-align", "center")
        .append("xhtml:strong")
        .style("background-color", model.colors.whiteAlpha[900])

      resultDateLabel
        .merge(resultDateLabel)
        .style("background-color", model.colors.whiteAlpha[900])

      resultTooltip = resultHolder
        .append("foreignObject")
        .attr("x", 5)
        .attr("y", 30)
        .attr("width", model.resultTooltipWidth)
        .attr("height", 500)

      resultTooltipWrapper = resultTooltip
        .append("xhtml:div")
        .style("margin", "1rem")
        .style("background-color", model.colors.gray[700])
        .style("color", model.colors.whiteAlpha[900])
        .style("box-shadow", "0 4px 16px rgba(0,0,0,.15)")
        .style("border-radius", "0.25rem")
        .style("padding", "0.25rem 0.5rem 0.5rem")

      resultTooltipList = resultTooltipWrapper.append("div")
    }

    resize()
    if (isBrowser) {
      select(window).on(`resize.${chartId}`, debounce(resize, 50))
      if (model.showLineTooltip)
        select("body").on(`click.${chartId}`, () => {
          if (closest(event.target, el => el.id === chartId)) return
          if (parseInt(resultHolder.style("opacity"), 10))
            resultHolder.style("opacity", 0)
        })
    }
  }

  const destroy = () => {
    while (element.firstChild) element.removeChild(element.firstChild)
    if (isBrowser) {
      select(window).on(`resize.${chartId}`, null)
      select("body").on(`click.${chartId}`, null)
    }
  }

  return {
    create,
    update,
    destroy,
  }
}

export default lineChart
